import { forwardRef, useImperativeHandle, useState } from "react";
import ReactDOM from "react-dom";
import IC_MODAL_CLOSE from "@/assets/simulInter/ic_modal_close.svg?react";
import deviceStore from "@/store/device.store";
import { message } from "antd";

interface IPConnectModalProps {
  streamName: string;
  showTrigger?: boolean;
  handleConnectByCode?: () => void;
  onConnectSuccess?: (isStart: boolean, boardStreamName?: string) => void;
  onClose?: (needToHome: boolean) => void; // 添加关闭回调
}

export interface IPConnectModalRef {
  open: () => void;
  close: () => void;
}

const IPConnectModal = forwardRef<IPConnectModalRef, IPConnectModalProps>(
  (props, ref) => {
    const { showTrigger = false, handleConnectByCode, onConnectSuccess, onClose } = props;
    const [visible, setVisible] = useState(false);
    const [ip, setIp] = useState("");
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClose = async (needToHome: boolean = false) => {
      setVisible(false);
      // 关闭时重置状态
      setIp("");
      setError(false);
      setLoading(false);
      // 调用外部关闭回调
      onClose?.(needToHome);
    };

    const open = () => {
      setVisible(true);
    };

    // 处理IP输入变化
    const handleIpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setIp(value);
      // 清除错误状态
      if (error) {
        setError(false);
      }
    };

    const handleConnect = async () => {
      // 防止重复点击
      if (loading) {
        return;
      }

      // 验证 IP 格式
      const ipAndPort = ip.split(':');
      const trimmedIp = ipAndPort[0];
      const port = ipAndPort[1] || '2998';
      if (!trimmedIp) {
        setError(true);
        message.error("请输入设备IP地址");
        return;
      }

      // 简单的 IP 格式验证
      const ipRegex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (!ipRegex.test(trimmedIp)) {
        setError(true);
        message.error("IP地址错误，请检查后重试");
        return;
      }

      setLoading(true);
      setError(false);

      // 使用通用连接方法
      const result = await deviceStore.getState().connectDeviceByIP(trimmedIp, port);

      if (!result.success) {
        setError(true);
        message.error(result.errorMsg || "连接失败，请检查网络后重试");
        setLoading(false);
        return;
      }

      // 连接成功，关闭弹窗
      setLoading(false);
      handleClose();

      // 回调通知父组件
      onConnectSuccess && onConnectSuccess(
        result.isRecording || false,
        result.streamName
      );
    };

    useImperativeHandle(ref, () => ({
      open,
      close: handleClose,
    }));

    return visible
      ? ReactDOM.createPortal(
        <div
          className={`fixed z-1 w-full h-full inset-0 flex items-center justify-center z-999 bg-black bg-opacity-50`}
        >
          <div className="flex flex-col items-center bg-white w-130 rounded-4 shadow relative overflow-hidden">
            <div
              className="absolute right-3 top-3 cursor-pointer"
              onClick={() => handleClose(true)}
            >
              <IC_MODAL_CLOSE className="w-8 h-8" />
            </div>
            <div className="mt-16 font-600 text-4.5 lh-7.5 text-mx-secondary">
              输入设备「IP地址」，连接设备
            </div>
            <div className="mt-1 text-#626773CC/80">
              确保连接设备在同一局域网络下
            </div>
            <input
              className={`mt-6 border-1px border-solid rounded-1.5 w-100 h-12 px-7.25 py-3 focus:outline-none lh-6 text-#30343C text-4 lh-6 text-center placeholder-#30343C/30 ${error ? "border-#F5222D" : "border-#DBDEE4"
                }`}
              value={ip}
              onChange={handleIpChange}
              placeholder="例如: 192.168.1.100"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConnect();
                }
              }}
            />
            <div
              className={`mt-8 w-36.5 h-12 flex items-center justify-center rd-2.5 text-white text-3.5 font-500 ${loading
                ? "bg-mx-primary/60 cursor-not-allowed"
                : "bg-mx-primary cursor-pointer hover:bg-mx-primary-hover"
                } ${showTrigger ? "mb-15" : "mb-12"}`}
              onClick={handleConnect}
            >
              {loading ? "连接中..." : "连 接"}
            </div>
            {showTrigger && (
              <div
                className="mb-6 text-3 text-mx-primary cursor-pointer lh-5"
                onClick={handleConnectByCode}
              >
                使用连接码连接
              </div>
            )}
          </div>
        </div>,
        document.body
      )
      : null;
  }
);

export default IPConnectModal;
